import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';
import { 
  FaUsers, 
  FaRoute, 
  FaCreditCard, 
  FaChartLine,
  FaArrowRight,
  FaCheck
} from 'react-icons/fa';

// Styled Components
const ShowcaseContainer = styled.div`
  padding: 6rem 2rem;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 4rem 1rem;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  position: relative;
  z-index: 2;
`;

const Title = styled.h2`
  font-size: 3.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #1e293b 0%, #475569 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.25rem;
  color: #64748b;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const TabsContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 3rem;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  border-radius: 50px;
  padding: 8px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  max-width: fit-content;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    border-radius: 20px;
    gap: 4px;
  }
`;

const Tab = styled.button<{ active: boolean }>`
  padding: 12px 24px;
  border: none;
  border-radius: 40px;
  background: ${props => props.active ? 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' : 'transparent'};
  color: ${props => props.active ? '#fff' : '#64748b'};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.95rem;

  &:hover {
    background: ${props => props.active ? 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' : 'rgba(79, 70, 229, 0.1)'};
    color: ${props => props.active ? '#fff' : '#4f46e5'};
  }

  @media (max-width: 768px) {
    padding: 10px 16px;
    font-size: 0.85rem;
  }
`;

const ContentContainer = styled.div`
  position: relative;
  max-width: 1200px;
  margin: 0 auto;
`;

const ServiceCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  padding: 3rem;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #4f46e5, #7c3aed, #ec4899);
  }

  @media (max-width: 768px) {
    padding: 2rem;
  }
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const CardContent = styled.div`
  h3 {
    font-size: 2.5rem;
    font-weight: 700;
    margin-bottom: 1rem;
    background: linear-gradient(135deg, #1e293b 0%, #475569 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;

    @media (max-width: 768px) {
      font-size: 2rem;
    }
  }

  h4 {
    font-size: 1.25rem;
    color: #4f46e5;
    margin-bottom: 1.5rem;
    font-weight: 600;
  }

  p {
    font-size: 1.1rem;
    color: #64748b;
    line-height: 1.7;
    margin-bottom: 2rem;
  }
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin-bottom: 2rem;

  li {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 12px;
    font-size: 1rem;
    color: #475569;

    svg {
      color: #10b981;
      flex-shrink: 0;
    }
  }
`;

const CTAButton = styled(motion.button)`
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
  color: white;
  border: none;
  padding: 16px 32px;
  border-radius: 50px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 10px 30px rgba(79, 70, 229, 0.3);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 15px 40px rgba(79, 70, 229, 0.4);
    transform: translateY(-2px);
  }
`;

const ImageContainer = styled.div`
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.15);

  img {
    width: 100%;
    height: auto;
    display: block;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(79, 70, 229, 0.1) 0%, rgba(124, 58, 237, 0.1) 100%);
    pointer-events: none;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;



// Service data
const services = [
  {
    id: 'team',
    title: 'Team Management',
    subtitle: 'Accurate Time Tracking',
    description: 'Modernize time tracking with digital clock-ins for accurate billing and payroll. Easily manage entries and keep records precise with real-time monitoring.',
    features: [
      'Digital clock-in/out system',
      'Real-time location tracking',
      'Automated timesheet generation',
      'Payroll integration',
      'Performance analytics'
    ],
    icon: FaUsers,
    image: '/api/placeholder/500/400'
  },
  {
    id: 'fleet',
    title: 'Fleet Optimization',
    subtitle: 'Smart Route Planning',
    description: 'Optimize your fleet operations with intelligent route planning, vehicle tracking, and maintenance scheduling. Reduce costs and improve efficiency.',
    features: [
      'GPS vehicle tracking',
      'Route optimization',
      'Fuel consumption monitoring',
      'Maintenance scheduling',
      'Driver performance metrics'
    ],
    icon: FaRoute,
    image: '/api/placeholder/500/400'
  },
  {
    id: 'sales',
    title: 'Sales & Payments',
    subtitle: 'Smart Estimates',
    description: 'Streamline your sales process with intelligent estimates, automated invoicing, and seamless payment processing. Get paid faster and grow your business.',
    features: [
      'Automated estimate generation',
      'Digital invoicing',
      'Multiple payment options',
      'Revenue tracking',
      'Customer payment history'
    ],
    icon: FaCreditCard,
    image: '/api/placeholder/500/400'
  },
  {
    id: 'analytics',
    title: 'Business Analytics',
    subtitle: 'Data-Driven Insights',
    description: 'Make informed decisions with comprehensive analytics and reporting. Track performance, identify trends, and optimize your operations.',
    features: [
      'Real-time dashboards',
      'Custom reports',
      'Performance metrics',
      'Trend analysis',
      'Predictive insights'
    ],
    icon: FaChartLine,
    image: '/api/placeholder/500/400'
  }
];



interface ServiceShowcaseProps {
  className?: string;
}

const ServiceShowcase: React.FC<ServiceShowcaseProps> = ({ className }) => {
  const [activeTab, setActiveTab] = useState('team');
  const [isPaused, setIsPaused] = useState(false);

  // Auto-cycle through tabs
  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      setActiveTab(currentTab => {
        const currentIndex = services.findIndex(service => service.id === currentTab);
        const nextIndex = (currentIndex + 1) % services.length;
        return services[nextIndex].id;
      });
    }, 4000); // Change tab every 4 seconds

    return () => clearInterval(interval);
  }, [isPaused]);

  const activeService = services.find(service => service.id === activeTab) || services[0];

  const handleTabClick = (tabId: string) => {
    setActiveTab(tabId);
    setIsPaused(true);
    // Resume auto-cycling after 8 seconds of user interaction
    setTimeout(() => setIsPaused(false), 8000);
  };

  return (
    <ShowcaseContainer className={className}>

      <SectionHeader>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <Title>
            <span style={{ 
              background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #ec4899 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              Smart Tools
            </span>{' '}
            for Seamless Service
          </Title>
          <Subtitle>
            With ServTrackr, you get more than just software — you get a smarter way to manage every aspect of your service business.
          </Subtitle>
        </motion.div>
      </SectionHeader>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        viewport={{ once: true }}
      >
        <TabsContainer>
          {services.map((service) => (
            <Tab
              key={service.id}
              active={activeTab === service.id}
              onClick={() => handleTabClick(service.id)}
            >
              <service.icon />
              {service.title}
            </Tab>
          ))}
        </TabsContainer>
      </motion.div>

      <ContentContainer>
        <AnimatePresence mode="wait">
          <ServiceCard
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
          >
            <CardGrid>
              <CardContent>
                <h3>{activeService.title}</h3>
                <h4>{activeService.subtitle}</h4>
                <p>{activeService.description}</p>
                
                <FeatureList>
                  {activeService.features.map((feature, index) => (
                    <motion.li
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                    >
                      <FaCheck />
                      {feature}
                    </motion.li>
                  ))}
                </FeatureList>

                <CTAButton
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Learn More
                  <FaArrowRight />
                </CTAButton>
              </CardContent>

              <ImageContainer>
                <img
                  src={activeService.image}
                  alt={activeService.title}
                />
              </ImageContainer>
            </CardGrid>
          </ServiceCard>
        </AnimatePresence>
      </ContentContainer>
    </ShowcaseContainer>
  );
};

export default ServiceShowcase;
